import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Slide,
  Tabs,
  Tab,
  Accordion,
  Chip,
  Typography,
  AccordionSummary,
  Modal,
  AccordionDetails,
  useMediaQuery,
} from '@mui/material';
import { pxToRem, pxTovW } from '../../../../commonUtils/resizeUtils';
import { IStyles } from '../../../../commonUtils/styleUtils';
import { IconWrapper } from '../../../elements/IconWrapper/Index';
import { useState, useMemo } from 'react';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { SubjectListItem } from '..';
import PrimaryButton from '../../../elements/PrimaryButton';
import { theme } from '../../../../theme/themeProvider';

const styles: IStyles = {
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100vw',
    marginY: { xs: pxToRem(10), md: pxTovW(70) },
    boxSizing: 'border-box',
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    gap: { xs: pxToRem(40), md: pxTovW(20) },
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'center', md: 'flex-start' },
    width: { xs: '95%', md: 'unset' },
  },
  contentBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(15), md: pxTovW(25) },
    backgroundColor: 'white',
    width: { xs: '95%', md: pxTovW(821) },
    borderRadius: { xs: pxToRem(10), md: pxTovW(20) },
    // paddingX: { xs: pxToRem(10), md: pxTovW(35) },
    paddingY: { xs: pxToRem(10), md: pxTovW(30) },
    maxHeight: { xs: '85vh', md: '80vh' },
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  topContainer: {
    paddingX: { xs: pxToRem(10), md: pxTovW(35) },
    borderBottom: `1px solid #707070`,
    paddingBottom: { xs: pxToRem(10), md: pxTovW(20) },
  },
  headingBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(20), md: pxTovW(40) },
    boxSizing: 'border-box',
  },
  headingImageBox: {
    width: { xs: pxToRem(80), md: pxTovW(110) },
    height: { xs: pxToRem(80), md: pxTovW(110) },
    borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
    backgroundColor: '#E7F4FF',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headingImage: {
    width: '90%',
    height: '90%',
  },
  ellipsisHeading: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
    maxWidth: { xs: '70%', md: '80%' },
  },
  tabs: {
    backgroundColor: 'white',
    padding: '0',
    '& .MuiTabs-flexContainer': {
      flexWrap: 'wrap',
      // rowGap: { xs: pxToRem(10), md: pxTovW(10) },
      // columnGap: { xs: pxToRem(10), md: pxTovW(10) },
      justifyContent: 'center',
    },
    '& .MuiTabs-indicator': { display: 'none' },
    '& .MuiTab-root': {
      textTransform: 'none',
      borderRadius: { xs: pxToRem(7), md: pxTovW(7) },
      minHeight: { xs: pxToRem(43), md: pxTovW(43) },
      fontWeight: 'regular',
      color: 'text.secondary',
      fontSize: { xs: pxToRem(16), md: pxTovW(21) },
      paddingX: { xs: pxToRem(10), md: pxTovW(10) },
    },
    '& .Mui-selected': {
      bgcolor: '#007CDC',
      color: 'white !important',
    },
  },
  bottomContainer: {
    paddingX: { xs: pxToRem(10), md: pxTovW(35) },
  },
  accordion: {
    backgroundColor: '#F5FAFF',
    border: '2px solid #A8D3FC',
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    marginBottom: { xs: pxToRem(10), md: pxTovW(20) },
    minHeight: { xs: pxToRem(50), md: pxTovW(80) },
    '&:before': { display: 'none' },
    '&.Mui-expanded': {
      margin: 0,
      marginBottom: { xs: pxToRem(10), md: pxTovW(20) },
      position: 'relative',
      zIndex: 1,
    },
    '&:first-of-type': {
      borderTopLeftRadius: { xs: pxToRem(10), md: pxTovW(10) },
      borderTopRightRadius: { xs: pxToRem(10), md: pxTovW(10) },
    },
    '&:last-of-type': {
      borderBottomLeftRadius: { xs: pxToRem(10), md: pxTovW(10) },
      borderBottomRightRadius: { xs: pxToRem(10), md: pxTovW(10) },
    },
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  chip: {
    bgcolor: '#007CDC',
    color: 'white',
    fontWeight: 600,
    fontSize: { xs: pxToRem(14), md: pxTovW(18) },
    paddingX: { xs: pxToRem(5), md: pxTovW(15) },
    height: { xs: pxToRem(25), md: pxTovW(35) },
  },
  accordionDetails: {
    padding: 0,
  },
  topicBox: {
    paddingX: { xs: pxToRem(10), md: pxTovW(20) },
    paddingY: { xs: pxToRem(10), md: pxTovW(15) },
    borderTop: '1px solid #A8D3FC',
  },
  okButton: {
    width: { xs: '70%', md: pxTovW(400) },
    display: 'flex',
    marginTop: { xs: pxToRem(20), md: pxTovW(30) },
    margin: 'auto',
  },
};

interface IProps {
  popupOpen: boolean;
  popupCloseHandler: () => void;
  subjectList?: SubjectListItem[];
  assessmentData?: AssessmentTask;
}

export const SubjectChapterTopicPopup = (props: IProps) => {
  const { popupOpen, popupCloseHandler, subjectList, assessmentData } = props;

  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedAccordion, setExpandedAccordion] = useState<number | false>(
    false
  );
  const selectedSubjectId = subjectList?.[selectedTab]?.id;

  // ----------------------------------------------------

  const chaptersForSubject = useMemo(() => {
    const allChapters =
      assessmentData?.taskMetaInfo?.chapterInfo?.chapterTopicsInfo ?? [];
    if (selectedSubjectId == null) return [];
    return allChapters.filter(
      (ch) => (ch as { subjectId?: number }).subjectId === selectedSubjectId
    );
  }, [
    assessmentData?.taskMetaInfo?.chapterInfo?.chapterTopicsInfo,
    selectedSubjectId,
  ]);

  // ----------------------------------------------------

  const handleTabChange = (_: React.SyntheticEvent, index: number) => {
    setSelectedTab(index);
    setExpandedAccordion(false);
  };

  // ----------------------------------------------------

  return (
    <Modal open={popupOpen} onClose={popupCloseHandler}>
      <Slide
        direction="up"
        in={popupOpen}
        mountOnEnter
        unmountOnExit
        timeout={500}
      >
        <Box sx={styles.root}>
          <Box sx={styles.container}>
            {/* //! Popup Body */}
            <Box sx={styles.contentBox}>
              <Box sx={styles.topContainer}>
                <Box sx={styles.headingBox}>
                  <Box sx={styles.headingImageBox}>
                    <IconWrapper
                      name="multi-subject-connection"
                      parentFolder="icons"
                      type="png"
                      customSx={styles.headingImage}
                    />
                  </Box>
                  <Typography variant="h2" sx={styles.ellipsisHeading}>
                    {assessmentData?.assessmentTitle}
                  </Typography>
                </Box>

                {/* //! Tabs */}
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  sx={styles.tabs}
                >
                  {(subjectList ?? []).map((s, i) => (
                    <Tab key={s.id} label={s.subjectName} value={i} />
                  ))}
                </Tabs>
              </Box>

              <Box sx={styles.bottomContainer}>
                {chaptersForSubject.map((chapter, index) => (
                  <Accordion
                    key={chapter.chapterId}
                    elevation={0}
                    expanded={expandedAccordion === index}
                    onChange={() =>
                      setExpandedAccordion(
                        expandedAccordion === index ? false : index
                      )
                    }
                    sx={styles.accordion}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={styles.accordionSummary}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: pxToRem(14), md: pxTovW(24) },
                          fontWeight: 'bold',
                        }}
                      >
                        {chapter.chapterNo}. {chapter.name}
                      </Typography>
                      <Chip
                        label={'Total Topics: ' + chapter.topicInfo.length}
                        size="small"
                        sx={styles.chip}
                      />
                    </AccordionSummary>

                    {/* //! TOPICS Details */}
                    <AccordionDetails sx={styles.accordionDetails}>
                      {chapter.topicInfo.map((topic, topicIndex) => (
                        <Box sx={styles.topicBox}>
                          <Typography variant="h4" key={topicIndex}>
                            {topic.name}
                          </Typography>
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}

                {/* //! OK Button */}
                <PrimaryButton onClick={popupCloseHandler} sx={styles.okButton}>
                  <Typography variant="h2" color="common.white">
                    OK
                  </Typography>
                </PrimaryButton>
              </Box>
            </Box>

            {/* //! X Button */}
            <Button onClick={popupCloseHandler} sx={{ p: '0px' }}>
              <IconWrapper
                name="close-yellow"
                size="md"
                parentFolder="icons"
                type="png"
                customSx={{
                  width: { xs: pxToRem(33), md: pxTovW(54) },
                  height: { xs: pxToRem(33), md: pxTovW(54) },
                }}
              />
            </Button>
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
};
